import { createReactComponentFromLit } from '@affine/component';
import { getViewManager } from '@affine/core/blocksuite/manager/view';
import type { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { PeekViewProvider } from '@blocksuite/affine/components/peek';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { RefNodeSlotsProvider } from '@blocksuite/affine/inlines/reference';
import type { ColorScheme } from '@blocksuite/affine/model';
import {
  codeBlockWrapMiddleware,
  defaultImageProxyMiddleware,
  ImageProxyService,
} from '@blocksuite/affine/shared/adapters';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import {
  BlockStdScope,
  BlockViewIdentifier,
  type EditorHost,
  ShadowlessElement,
} from '@blocksuite/affine/std';
import type {
  ExtensionType,
  Query,
  Store,
  TransformerMiddleware,
} from '@blocksuite/affine/store';
import type { Signal } from '@preact/signals-core';
import { css, html, /* nothing, */ type PropertyValues } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { keyed } from 'lit/directives/keyed.js';
import { literal } from 'lit/static-html.js';
import React from 'react';
import { filter } from 'rxjs/operators';

import { markDownToDoc } from '../../utils';
import type { AffineAIPanelState } from '../widgets/ai-panel/type';
import {
  markedRendererStyles,
  MarkedStreamingRenderer,
} from './text-renderer/index';

export const getCustomPageEditorBlockSpecs: () => ExtensionType[] = () => {
  const manager = getViewManager().config.init().value;
  return [
    ...manager.get('page'),
    {
      setup: di => {
        di.override(
          BlockViewIdentifier('affine:page'),
          () => literal`affine-page-root`
        );
      },
    },
  ];
};

const customHeadingStyles = css`
  .custom-heading {
    .h1 {
      font-size: calc(var(--streamlain-font-h-1) - 2px);
      code {
        font-size: calc(var(--streamlain-font-base) + 6px);
      }
    }
    .h2 {
      font-size: calc(var(--streamlain-font-h-2) - 2px);
      code {
        font-size: calc(var(--streamlain-font-base) + 4px);
      }
    }
    .h3 {
      font-size: calc(var(--streamlain-font-h-3) - 2px);
      code {
        font-size: calc(var(--streamlain-font-base) + 2px);
      }
    }
    .h4 {
      font-size: calc(var(--streamlain-font-h-4) - 2px);
      code {
        font-size: var(--streamlain-font-base);
      }
    }
    .h5 {
      font-size: calc(var(--streamlain-font-h-5) - 2px);
      code {
        font-size: calc(var(--streamlain-font-base) - 2px);
      }
    }
    .h6 {
      font-size: calc(var(--streamlain-font-h-6) - 2px);
      code {
        font-size: calc(var(--streamlain-font-base) - 4px);
      }
    }
  }
`;

export type TextRendererOptions = {
  customHeading?: boolean;
  extensions?: ExtensionType[];
  additionalMiddlewares?: TransformerMiddleware[];
  testId?: string;
  affineFeatureFlagService?: FeatureFlagService;
  theme?: Signal<ColorScheme>;
  renderMode?: 'blocksuite' | 'markdown';
};

// todo: refactor it for more general purpose usage instead of AI only?
export class TextRenderer extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = [
    markedRendererStyles,
    css`
      .ai-answer-text-editor.affine-page-viewport {
        background: transparent;
        font-family: var(--streamlain-font-family);
        margin-top: 0;
        margin-bottom: 0;
        --streamlain-ai-chat-selection-background: rgba(30, 150, 235, 0.34);
        --streamlain-ai-chat-selection-background: color-mix(
          in srgb,
          var(--streamlain-v2-icon-activated, #1e96eb) 42%,
          transparent
        );
        --streamlain-ai-chat-selection-color: var(
          --streamlain-text-primary-color,
          ${unsafeCSSVarV2('text/primary')}
        );
      }

      .ai-answer-text-editor .affine-page-root-block-container {
        padding: 0;
        margin: 0;
        line-height: var(--streamlain-line-height);
        color: ${unsafeCSSVarV2('text/primary')};
        font-weight: 400;
      }

      .ai-answer-text-editor {
        .affine-note-block-container {
          > .affine-block-children-container {
            > :first-child:not(affine-callout),
            > :first-child:not(affine-callout) * {
              margin-top: 0 !important;
            }
            > :last-child,
            > :last-child * {
              margin-bottom: 0 !important;
            }
          }
        }

        .affine-paragraph-block-container {
          line-height: 22px;

          .h6 {
            padding-left: 16px;
            color: ${unsafeCSSVarV2('text/link')};
            font-size: var(--streamlain-font-base);

            .toggle-icon {
              transform: translateX(0);
              svg {
                color: ${unsafeCSSVarV2('text/link')};
              }
            }
          }
        }
      }

      .text-renderer-container {
        overflow-y: visible;
        overflow-x: hidden;
        padding: 0;
      }

      .text-renderer-container.show-scrollbar::-webkit-scrollbar {
        width: 5px;
        height: 100px;
      }
      .text-renderer-container.show-scrollbar::-webkit-scrollbar-thumb {
        border-radius: 20px;
      }
      .text-renderer-container.show-scrollbar:hover::-webkit-scrollbar-thumb {
        background-color: var(--streamlain-black-30);
      }
      .text-renderer-container.show-scrollbar::-webkit-scrollbar-corner {
        display: none;
      }

      .text-renderer-container {
        rich-text .nowrap-lines v-text span,
        rich-text .nowrap-lines v-element span {
          white-space: pre;
        }
        editor-host:focus-visible {
          outline: none;
        }
        editor-host * {
          box-sizing: border-box;
        }
        editor-host {
          isolation: isolate;
        }
      }

      .ai-answer-text-editor ::selection,
      .ai-answer-text-editor::selection,
      .streaming-markdown-content ::selection,
      .streaming-markdown-content::selection {
        color: var(--streamlain-ai-chat-selection-color);
        background: var(--streamlain-ai-chat-selection-background);
        text-shadow: none;
      }

      ${customHeadingStyles}
    `,
  ];

  private _answers: string[] = [];

  private _maxContainerHeight = 0;

  /**
   * Lightweight renderer for streaming mode (SOLID: Single Responsibility).
   * Used during 'generating' state for instant, synchronous rendering.
   */
  private readonly _streamingRenderer = new MarkedStreamingRenderer();

  /**
   * Current markdown content for streaming renderer.
   */
  private _currentMarkdown = '';

  private readonly _clearTimer = () => {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  };

  private _doc: Store | null = null;

  private _host: EditorHost | null = null;

  private readonly _query: Query = {
    mode: 'strict',
    match: [
      'affine:page',
      'affine:note',
      'affine:table',
      'affine:surface',
      'affine:paragraph',
      'affine:callout',
      'affine:code',
      'affine:list',
      'affine:divider',
      'affine:latex',
      'affine:bookmark',
      'affine:attachment',
      'affine:embed-linked-doc',
    ].map(flavour => ({ flavour, viewType: 'display' })),
  };

  private _timer?: ReturnType<typeof setInterval> | null = null;

  private readonly _subscribeDocLinkClicked = () => {
    const refNodeSlots = this._host?.std.getOptional(RefNodeSlotsProvider);
    if (!refNodeSlots) return;
    this.disposables.add(
      refNodeSlots.docLinkClicked
        .pipe(
          filter(
            options => !!this._previewHost && options.host === this._previewHost
          )
        )
        .subscribe(options => {
          // Open the doc in center peek
          this._host?.std
            .getOptional(PeekViewProvider)
            ?.peek({
              docId: options.pageId,
            })
            .catch(console.error);
        })
    );
  };

  private readonly _updateDoc = () => {
    if (this.options.renderMode === 'markdown') {
      return;
    }
    if (this._answers.length > 0) {
      const latestAnswer = this._answers.pop();
      this._answers = [];
      if (latestAnswer) {
        const middlewares = [
          defaultImageProxyMiddleware,
          codeBlockWrapMiddleware(true),
          ...(this.options.additionalMiddlewares ?? []),
        ];
        markDownToDoc(
          latestAnswer,
          middlewares,
          this.options.affineFeatureFlagService
        )
          .then(doc => {
            this.disposeDoc();
            this._doc = doc.doc.getStore({
              query: this._query,
            });
            this._host = new BlockStdScope({
              store: this._doc,
              extensions:
                this.options.extensions ?? getCustomPageEditorBlockSpecs(),
            }).render();
            this.disposables.add(() => {
              doc.doc.removeStore({ query: this._query });
            });
            this._doc.readonly = true;
            // Always load the doc to show formatted content during streaming
            this._doc.load();
            this.requestUpdate();
            if (this.state !== 'generating') {
              const imageProxyService = this._host.std.get(ImageProxyService);
              imageProxyService.setImageProxyURL(
                imageProxyService.imageProxyURL
              );
              this._clearTimer();
            }
          })
          .catch(console.error);
      }
    }
  };

  override connectedCallback() {
    super.connectedCallback();

    // Always set markdown for streaming renderer as fallback
    this._currentMarkdown = this.answer;

    if (this.options.renderMode === 'markdown') {
      return;
    }

    // If not streaming, also initialize BlockSuite renderer
    if (this.state !== 'generating') {
      this._answers.push(this.answer);
      this._updateDoc();
    }
  }

  override firstUpdated() {
    this._subscribeDocLinkClicked();
  }

  private disposeDoc() {
    this._doc?.dispose();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._clearTimer();
    this._streamingRenderer.dispose();
    this.disposeDoc();
  }

  override render() {
    const { customHeading, testId = 'ai-text-renderer' } = this.options;
    const classes = classMap({
      'text-renderer-container': true,
      'custom-heading': !!customHeading,
    });

    // Strategy Pattern: Use lightweight renderer during streaming,
    // full BlockSuite renderer after completion
    const isMarkdownOnly = this.options.renderMode === 'markdown';
    const isStreaming = this.state === 'generating';

    // Use streaming-markdown renderer for:
    // 1. Streaming mode (instant updates, handles incomplete markdown)
    // 2. Fallback when BlockSuite doc is not yet ready
    if (isMarkdownOnly || isStreaming || !this._doc) {
      return html`
        <div class=${classes} data-testid=${testId}>
          <div class="ai-answer-text-editor affine-page-viewport">
            ${this._streamingRenderer.render(this._currentMarkdown)}
          </div>
        </div>
      `;
    }

    // Finished mode: Use BlockSuite renderer (full features)
    return html`
      <div class=${classes} data-testid=${testId}>
        ${keyed(
          this._doc,
          html`<div class="ai-answer-text-editor affine-page-viewport">
            ${this._host}
          </div>`
        )}
      </div>
    `;
  }

  override shouldUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has('answer')) {
      this._currentMarkdown = this.answer;
      if (this.options.renderMode === 'markdown') {
        return true;
      }
      // During streaming: Update markdown for lightweight renderer (instant)
      if (this.state === 'generating') {
        return true; // Re-render immediately with streaming-markdown
      }
      // After streaming: Queue for BlockSuite processing
      this._answers.push(this.answer);
      return false;
    }

    // State change from generating to finished: Trigger BlockSuite conversion
    if (
      this.options.renderMode !== 'markdown' &&
      changedProperties.has('state') &&
      this.state !== 'generating'
    ) {
      this._answers.push(this.answer);
      this._updateDoc();
    }

    return true;
  }

  override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    requestAnimationFrame(() => {
      if (!this._container) return;
      // Track max height during generation
      if (this.state === 'generating') {
        this._maxContainerHeight = Math.max(
          this._maxContainerHeight,
          this._container.scrollHeight
        );
        // Apply min-height to prevent shrinking
        this._container.style.minHeight = `${this._maxContainerHeight}px`;
      } else {
        setTimeout(() => {
          this._maxContainerHeight = 0;
          this._container.style.minHeight = '';
        }, 500);
      }
    });
  }

  @query('.text-renderer-container')
  private accessor _container!: HTMLDivElement;

  @query('.text-renderer-container editor-host')
  private accessor _previewHost: EditorHost | null = null;

  @property({ attribute: false })
  accessor answer!: string;

  @property({ attribute: false })
  accessor options!: TextRendererOptions;

  @property({ attribute: false })
  accessor state: AffineAIPanelState | undefined = undefined;
}

export const createTextRenderer = (options: TextRendererOptions) => {
  return (answer: string, state?: AffineAIPanelState) => {
    return html`<text-renderer
      contenteditable="false"
      .answer=${answer}
      .state=${state}
      .options=${options}
    ></text-renderer>`;
  };
};

export const LitTextRenderer = createReactComponentFromLit({
  react: React,
  elementClass: TextRenderer,
});

declare global {
  interface HTMLElementTagNameMap {
    'text-renderer': TextRenderer;
  }
}
